import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Minus, RefreshCw, Building2, AlertCircle,
  ArrowUpRight, ArrowDownRight, CheckCircle2, Shuffle, Plus, X,
} from 'lucide-react';
import { ExportDropdown } from '@/components/ExportDropdown';
import { HelpBubble } from '@/components/HelpBubble';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { TrustScoreGate } from '@/components/TrustScoreGate';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useCurrency } from '@/lib/useCurrency';
import { BenchmarkCountrySelect, AUTO, GLOBAL, loadCountryChoice, saveCountryChoice, countrySourceLabel } from '@/components/benchmark/BenchmarkCountrySelect';

const STATUS_COLORS = {
  over: { bg: 'bg-red-50 dark:bg-red-950/20', text: 'text-red-700 dark:text-red-400', badge: 'destructive' },
  under: { bg: 'bg-emerald-50 dark:bg-emerald-950/20', text: 'text-emerald-700 dark:text-emerald-400', badge: 'default' },
  aligned: { bg: 'bg-slate-50 dark:bg-slate-800/30', text: 'text-slate-600 dark:text-slate-400', badge: 'secondary' },
};

const COLORS = {
  org: 'hsl(var(--primary))',
  industryA: '#94a3b8',
  industryB: '#f59e0b',
};

export default function IndustryBenchmark() {
  const { symbol: cs } = useCurrency();
  const [data, setData] = useState(null);
  const [dualData, setDualData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dualLoading, setDualLoading] = useState(false);
  const [error, setError] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [secondIndustry, setSecondIndustry] = useState('');
  const [dualMode, setDualMode] = useState(false);
  const [orgIndustry, setOrgIndustry] = useState('');
  const [countryChoice, setCountryChoice] = useState(loadCountryChoice);
  const countryRef = useRef(countryChoice);
  const countryParam = () => (countryRef.current === AUTO ? {} : { compare_country: countryRef.current });

  const fetchIndustries = useCallback(async () => {
    try {
      const res = await api.get('/benchmark/industries');
      setIndustries(res.data.industries || []);
    } catch { /* ignore */ }
  }, []);

  const fetchBenchmark = useCallback(async (compareIndustry) => {
    setLoading(true);
    setError(null);
    setDualData(null);
    setDualMode(false);
    setSecondIndustry('');
    try {
      const params = { ...(compareIndustry ? { compare_industry: compareIndustry } : {}), ...countryParam() };
      const res = await api.get('/benchmark/analysis', { params });
      setData(res.data);
      if (res.data.org_industry && !orgIndustry) {
        setOrgIndustry(res.data.org_industry);
        if (!compareIndustry) setSelectedIndustry(res.data.org_industry);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load benchmark data');
    } finally {
      setLoading(false);
    }
  }, [orgIndustry]);

  const fetchDualBenchmark = useCallback(async (indA, indB) => {
    if (!indA || !indB || indA === indB) return;
    setDualLoading(true);
    try {
      const res = await api.get('/benchmark/dual-analysis', {
        params: { industry_a: indA, industry_b: indB, ...countryParam() },
      });
      setDualData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load dual benchmark');
    } finally {
      setDualLoading(false);
    }
  }, []);

  useEffect(() => { fetchIndustries(); fetchBenchmark(); }, [fetchIndustries, fetchBenchmark]);

  const handleIndustryChange = (value) => {
    setSelectedIndustry(value);
    if (dualMode && secondIndustry) {
      fetchDualBenchmark(value, secondIndustry);
    } else {
      fetchBenchmark(value);
    }
  };

  const handleSecondIndustryChange = (value) => {
    setSecondIndustry(value);
    if (value && selectedIndustry && value !== selectedIndustry) {
      fetchDualBenchmark(selectedIndustry, value);
    }
  };

  const handleCountryChange = (value) => {
    setCountryChoice(value);
    countryRef.current = value;
    saveCountryChoice(value);
    if (dualMode && secondIndustry) {
      fetchDualBenchmark(selectedIndustry, secondIndustry);
    } else {
      fetchBenchmark(selectedIndustry !== orgIndustry ? selectedIndustry : undefined);
    }
  };

  const enableDualMode = () => {
    setDualMode(true);
  };

  const disableDualMode = () => {
    setDualMode(false);
    setDualData(null);
    setSecondIndustry('');
  };

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
  };

  // ── Chart data ──
  const isDual = dualMode && dualData;

  const chartData = isDual
    ? (dualData.comparison || []).slice(0, 15).map(c => ({
        category: c.category?.length > 18 ? c.category.slice(0, 16) + '...' : c.category,
        fullCategory: c.category,
        'Your Spend %': c.org_pct,
        [`${dualData.industry_a} %`]: c.industry_a_pct,
        [`${dualData.industry_b} %`]: c.industry_b_pct,
      }))
    : (data?.comparison || []).slice(0, 15).map(c => ({
        category: c.category?.length > 18 ? c.category.slice(0, 16) + '...' : c.category,
        fullCategory: c.category,
        'Your Spend %': c.org_pct,
        'Industry Avg %': c.industry_pct,
      }));

  // ── Export ──
  const exportCSV = () => {
    if (isDual) {
      const rows = [
        ['Category', 'Your %', `${dualData.industry_a} %`, `${dualData.industry_b} %`, 'Delta A', 'Delta B'],
        ...(dualData.comparison || []).map(c => [c.category, c.org_pct, c.industry_a_pct, c.industry_b_pct, c.delta_a, c.delta_b]),
      ];
      downloadCSV(rows, 'dual_industry_benchmark');
    } else if (data) {
      const rows = [
        ['Category', 'Your %', 'Industry %', 'Delta %', 'Status', 'Insight'],
        ...(data.comparison || []).map(c => [c.category, c.org_pct, c.industry_pct, c.delta_pct, c.status, c.insight]),
      ];
      downloadCSV(rows, 'industry_benchmark');
    }
  };

  const exportPDF = () => {
    if (isDual) {
      buildPDF([{
        title: `Dual Benchmark: ${dualData.industry_a} vs ${dualData.industry_b}`,
        headers: ['Category', 'Your %', `${dualData.industry_a} %`, `${dualData.industry_b} %`, 'Delta A', 'Delta B'],
        rows: (dualData.comparison || []).map(c => [c.category, `${c.org_pct}%`, `${c.industry_a_pct}%`, `${c.industry_b_pct}%`, `${c.delta_a > 0 ? '+' : ''}${c.delta_a}%`, `${c.delta_b > 0 ? '+' : ''}${c.delta_b}%`]),
      }], 'Dual_Industry_Benchmark');
    } else if (data) {
      buildPDF([{
        title: `Industry Benchmark - ${data.industry}`,
        headers: ['Category', 'Your %', 'Industry %', 'Delta', 'Status'],
        rows: (data.comparison || []).map(c => [c.category, `${c.org_pct}%`, `${c.industry_pct}%`, `${c.delta_pct > 0 ? '+' : ''}${c.delta_pct}%`, c.status]),
      }], 'Industry_Benchmark');
    }
  };

  // ── Error state ──
  if (!loading && error) {
    const noIndustry = error.includes('Industry not set');
    return (
      <div className="space-y-8" data-testid="industry-benchmark-page">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Industry Benchmark</h1>
          <p className="text-muted-foreground mt-1">Compare your spend against industry norms</p>
        </div>
        <Card className="py-12">
          <CardContent className="text-center">
            {noIndustry ? (
              <>
                <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
                <p className="text-lg font-medium mb-2">Industry Not Set</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Set your industry on the Upload Data page to enable AI-powered benchmarking.
                </p>
                <Link to="/upload">
                  <Button data-testid="go-to-upload">Go to Upload Data</Button>
                </Link>
              </>
            ) : (
              <>
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive opacity-60" />
                <p className="text-lg font-medium mb-2">Error</p>
                <p className="text-sm text-muted-foreground mb-4">{error}</p>
                <Button onClick={() => fetchBenchmark()} data-testid="retry-button">Retry</Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeData = isDual ? dualData : data;

  return (
    <TrustScoreGate mode="hard" moduleLabel="Industry Benchmark">
    <div className="space-y-6" data-testid="industry-benchmark-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Industry Benchmark</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isDual
              ? <>Compare your spend against <span className="font-semibold text-primary">{dualData.industry_a}</span> and <span className="font-semibold text-amber-500">{dualData.industry_b}</span><span className="ml-1">in <span className="font-semibold" data-testid="benchmark-country-label">{dualData.country || GLOBAL}</span> <span className="text-xs">({countrySourceLabel(dualData)})</span></span></>
              : <>Compare your spend distribution against{data?.industry && <span className="font-semibold text-primary ml-1">{data.industry}</span>} industry norms{data && <span className="ml-1">in <span className="font-semibold" data-testid="benchmark-country-label">{data.country || GLOBAL}</span> <span className="text-xs" data-testid="benchmark-country-source">({countrySourceLabel(data)})</span></span>}</>
            }
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="benchmark-toolbar">
          <TrustScoreBadge pageName="benchmark" />
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/30 p-1" data-testid="industry-controls-group">
          <Select value={selectedIndustry} onValueChange={handleIndustryChange}>
            <SelectTrigger className="w-[200px] h-7 text-xs bg-background" data-testid="industry-selector">
              <Shuffle className="w-3.5 h-3.5 mr-1.5 text-muted-foreground shrink-0" />
              <SelectValue placeholder="Select industry..." />
            </SelectTrigger>
            <SelectContent>
              {industries.map((ind) => (
                <SelectItem key={ind} value={ind} data-testid={`industry-option-${ind.replace(/\s+/g, '-').toLowerCase()}`}>
                  {ind}{ind === orgIndustry ? ' (yours)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!dualMode ? (
            <Button variant="ghost" size="sm" onClick={enableDualMode} className="h-7 text-xs" data-testid="add-comparison-button">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Industry
            </Button>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground font-medium">vs</span>
              <Select value={secondIndustry} onValueChange={handleSecondIndustryChange}>
                <SelectTrigger className="w-[200px] h-7 text-xs bg-background border-amber-300 dark:border-amber-700" data-testid="second-industry-selector">
                  <SelectValue placeholder="Second industry..." />
                </SelectTrigger>
                <SelectContent>
                  {industries.filter(i => i !== selectedIndustry).map((ind) => (
                    <SelectItem key={ind} value={ind} data-testid={`second-industry-option-${ind.replace(/\s+/g, '-').toLowerCase()}`}>
                      {ind}{ind === orgIndustry ? ' (yours)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={disableDualMode} data-testid="remove-comparison-button">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
          </div>
          <BenchmarkCountrySelect value={countryChoice} options={data?.country_options} orgCountry={data?.org_country} onChange={handleCountryChange} />
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button
            variant="outline" size="sm" className="h-8"
            onClick={() => isDual ? fetchDualBenchmark(selectedIndustry, secondIndustry) : fetchBenchmark(selectedIndustry)}
            disabled={loading || dualLoading}
            data-testid="refresh-benchmark"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${(loading || dualLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Loading */}
      {(loading || dualLoading) && (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-sm text-muted-foreground">
            {dualLoading ? 'Generating dual-industry benchmarks...' : 'Generating AI-powered benchmarks...'}
          </p>
        </div>
      )}

      {!loading && !dualLoading && activeData && (
        <>
          {/* Dual mode banner */}
          {isDual && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20" data-testid="dual-industry-banner">
                <CardContent className="py-3 px-4 flex items-center gap-3 text-sm">
                  <Shuffle className="w-4 h-4 text-amber-500 shrink-0" />
                  <span>
                    <strong>Dual-industry comparison:</strong> Your spend is benchmarked against both{' '}
                    <span className="font-semibold" style={{ color: COLORS.industryA }}>{dualData.industry_a}</span> and{' '}
                    <span className="font-semibold text-amber-500">{dualData.industry_b}</span> industry norms side-by-side.
                  </span>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Cross-industry banner (single mode) */}
          {!isDual && data?.is_cross_industry && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20" data-testid="cross-industry-banner">
                <CardContent className="py-3 px-4 flex items-center gap-3 text-sm">
                  <Shuffle className="w-4 h-4 text-indigo-500 shrink-0" />
                  <span>
                    <strong>Cross-industry comparison:</strong> Your <span className="font-semibold text-primary">{data.org_industry}</span> spend is being compared against <span className="font-semibold text-primary">{data.industry}</span> industry norms.
                  </span>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* ── KPI Cards ── */}
          {isDual ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Industry A summary */}
              <Card data-testid="kpi-dual-a">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.industryA }} />
                    <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{dualData.industry_a}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-6 text-sm">
                    <div>
                      <span className="text-destructive font-bold text-lg">{dualData.summary_a.over}</span>
                      <span className="text-xs text-muted-foreground ml-1">over</span>
                    </div>
                    <div>
                      <span className="font-bold text-lg">{dualData.summary_a.aligned}</span>
                      <span className="text-xs text-muted-foreground ml-1">aligned</span>
                    </div>
                    <div>
                      <span className="text-emerald-600 font-bold text-lg">{dualData.summary_a.under}</span>
                      <span className="text-xs text-muted-foreground ml-1">under</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Industry B summary */}
              <Card data-testid="kpi-dual-b" className="border-amber-200/50 dark:border-amber-800/50">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{dualData.industry_b}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-6 text-sm">
                    <div>
                      <span className="text-destructive font-bold text-lg">{dualData.summary_b.over}</span>
                      <span className="text-xs text-muted-foreground ml-1">over</span>
                    </div>
                    <div>
                      <span className="font-bold text-lg">{dualData.summary_b.aligned}</span>
                      <span className="text-xs text-muted-foreground ml-1">aligned</span>
                    </div>
                    <div>
                      <span className="text-emerald-600 font-bold text-lg">{dualData.summary_b.under}</span>
                      <span className="text-xs text-muted-foreground ml-1">under</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Card data-testid="kpi-industry">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    {data?.is_cross_industry ? 'Comparing vs' : 'Industry'}
                    <HelpBubble text="The industry your spend mix is being compared against. Change this in Settings to reflect the vertical your buyer operates in." />
                  </CardTitle>
                  {data?.is_cross_industry ? <Shuffle className="w-5 h-5 text-indigo-500" /> : <Building2 className="w-5 h-5 text-primary" />}
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold">{data?.industry}</div>
                  <p className="text-xs text-muted-foreground mt-1">Total: {cs}{data?.grand_total?.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card data-testid="kpi-over">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Over-Spending <HelpBubble text="Categories where your share of total spend is materially higher than the industry norm. Investigate for over-scoping or premium pricing." /></CardTitle>
                  <ArrowUpRight className="w-5 h-5 text-destructive" />
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold text-destructive">{data?.summary?.over_spending}</div>
                  <p className="text-xs text-muted-foreground mt-1">categories above norm</p>
                </CardContent>
              </Card>
              <Card data-testid="kpi-aligned">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Aligned <HelpBubble text="Categories with spend share within ±3 percentage points of the industry norm. These are usually your best-managed categories." /></CardTitle>
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold">{data?.summary?.aligned}</div>
                  <p className="text-xs text-muted-foreground mt-1">categories within range</p>
                </CardContent>
              </Card>
              <Card data-testid="kpi-under">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Under-Spending <HelpBubble text="Categories where you're spending less as a share than the industry norm. Not always a win — investigate whether you're under-scoping a strategic area." /></CardTitle>
                  <ArrowDownRight className="w-5 h-5 text-emerald-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold text-emerald-600">{data?.summary?.under_spending}</div>
                  <p className="text-xs text-muted-foreground mt-1">categories below norm</p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* ── Chart ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="benchmark-chart">
              <CardHeader>
                <CardTitle>
                  {isDual
                    ? `Spend Distribution: You vs ${dualData.industry_a} vs ${dualData.industry_b}`
                    : `Spend Distribution: You vs ${data?.industry} Industry`}
                </CardTitle>
                <CardDescription>Percentage of total spend by category</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={isDual ? 500 : 450}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" tickFormatter={v => `${v}%`} />
                    <YAxis dataKey="category" type="category" width={150} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(val, name) => [`${val}%`, name]}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.fullCategory || label}
                    />
                    <Legend />
                    <Bar dataKey="Your Spend %" fill={COLORS.org} radius={[0, 4, 4, 0]} barSize={isDual ? 10 : 14} />
                    {isDual ? (
                      <>
                        <Bar dataKey={`${dualData.industry_a} %`} fill={COLORS.industryA} radius={[0, 4, 4, 0]} barSize={10} opacity={0.7} />
                        <Bar dataKey={`${dualData.industry_b} %`} fill={COLORS.industryB} radius={[0, 4, 4, 0]} barSize={10} opacity={0.7} />
                      </>
                    ) : (
                      <Bar dataKey="Industry Avg %" fill={COLORS.industryA} radius={[0, 4, 4, 0]} barSize={14} opacity={0.5} />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Table ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card data-testid="benchmark-table">
              <CardHeader>
                <CardTitle>Category-Level Benchmark Details</CardTitle>
                <CardDescription>
                  {isDual
                    ? `Side-by-side comparison against ${dualData.industry_a} and ${dualData.industry_b}`
                    : 'AI-generated insights for each spend category'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  {isDual ? (
                    <table className="w-full" data-testid="dual-benchmark-table">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-1.5 px-2 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                          <th className="text-right py-1.5 px-2 text-xs font-semibold text-muted-foreground uppercase">Your %</th>
                          <th className="text-right py-1.5 px-2 text-xs font-semibold uppercase" style={{ color: COLORS.industryA }}>{dualData.industry_a} %</th>
                          <th className="text-right py-1.5 px-2 text-xs font-semibold uppercase text-amber-500">{dualData.industry_b} %</th>
                          <th className="text-right py-1.5 px-2 text-xs font-semibold text-muted-foreground uppercase">Delta A</th>
                          <th className="text-right py-1.5 px-2 text-xs font-semibold text-muted-foreground uppercase">Delta B</th>
                          <th className="text-center py-1.5 px-2 text-xs font-semibold text-muted-foreground uppercase">Status A</th>
                          <th className="text-center py-1.5 px-2 text-xs font-semibold text-muted-foreground uppercase">Status B</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dualData.comparison || []).map(row => {
                          const scA = STATUS_COLORS[row.status_a] || STATUS_COLORS.aligned;
                          const scB = STATUS_COLORS[row.status_b] || STATUS_COLORS.aligned;
                          return (
                            <tr key={row.category} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                              <td className="py-1.5 px-2 font-medium text-sm">{row.category}</td>
                              <td className="py-1.5 px-2 text-right font-mono text-sm font-semibold">{row.org_pct}%</td>
                              <td className="py-1.5 px-2 text-right font-mono text-sm">{row.industry_a_pct}%</td>
                              <td className="py-1.5 px-2 text-right font-mono text-sm">{row.industry_b_pct}%</td>
                              <td className={cn('py-1.5 px-2 text-right font-mono text-sm font-semibold', scA.text)}>
                                {row.delta_a > 0 ? '+' : ''}{row.delta_a}%
                              </td>
                              <td className={cn('py-1.5 px-2 text-right font-mono text-sm font-semibold', scB.text)}>
                                {row.delta_b > 0 ? '+' : ''}{row.delta_b}%
                              </td>
                              <td className="py-1.5 px-2 text-center">
                                <Badge variant={scA.badge} className="text-[10px] capitalize">
                                  {row.status_a === 'over' && <ArrowUpRight className="w-3 h-3 mr-0.5" />}
                                  {row.status_a === 'under' && <ArrowDownRight className="w-3 h-3 mr-0.5" />}
                                  {row.status_a === 'aligned' && <Minus className="w-3 h-3 mr-0.5" />}
                                  {row.status_a}
                                </Badge>
                              </td>
                              <td className="py-1.5 px-2 text-center">
                                <Badge variant={scB.badge} className="text-[10px] capitalize">
                                  {row.status_b === 'over' && <ArrowUpRight className="w-3 h-3 mr-0.5" />}
                                  {row.status_b === 'under' && <ArrowDownRight className="w-3 h-3 mr-0.5" />}
                                  {row.status_b === 'aligned' && <Minus className="w-3 h-3 mr-0.5" />}
                                  {row.status_b}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                          <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Your %</th>
                          <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Industry %</th>
                          <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Delta</th>
                          <th className="text-center py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                          <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">AI Insight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.comparison || []).map(row => {
                          const sc = STATUS_COLORS[row.status] || STATUS_COLORS.aligned;
                          return (
                            <tr key={row.category} className={cn('border-b border-border hover:bg-muted/50 transition-colors', sc.bg)}>
                              <td className="py-1.5 px-3 font-medium text-sm">{row.category}</td>
                              <td className="py-1.5 px-3 text-right font-mono text-sm">{row.org_pct}%</td>
                              <td className="py-1.5 px-3 text-right font-mono text-sm">{row.industry_pct}%</td>
                              <td className={cn('py-1.5 px-3 text-right font-mono text-sm font-semibold', sc.text)}>
                                {row.delta_pct > 0 ? '+' : ''}{row.delta_pct}%
                              </td>
                              <td className="py-1.5 px-3 text-center">
                                <Badge variant={sc.badge} className="text-xs capitalize">
                                  {row.status === 'over' && <ArrowUpRight className="w-3 h-3 mr-1" />}
                                  {row.status === 'under' && <ArrowDownRight className="w-3 h-3 mr-1" />}
                                  {row.status === 'aligned' && <Minus className="w-3 h-3 mr-1" />}
                                  {row.status}
                                </Badge>
                              </td>
                              <td className="py-1.5 px-3 text-sm text-muted-foreground max-w-xs">{row.insight}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </>
      )}
      <AIPanel page="benchmark" filters={{}} />
    </div>
    </TrustScoreGate>
  );
}
